// In-memory store powering DEMO mode (no Firebase backend configured).
// Gives the storefront a live feel: placing an order decrements daily stock and
// pushes updates to subscribers, and the admin/orders board reads from here.
import { DAILY_SAMPLE } from "../data/dailySample";
import type { CartLine, DailyItem, Order } from "./types";

let daily: DailyItem[] = DAILY_SAMPLE.map((d) => ({ ...d }));
const dailyListeners = new Set<(items: DailyItem[]) => void>();

// Demo orders persist to localStorage so order tracking survives a page reload.
const ORDERS_KEY = "kb_demo_orders";
function loadOrders(): Order[] {
  try {
    const raw = localStorage.getItem(ORDERS_KEY);
    return raw ? (JSON.parse(raw) as Order[]) : [];
  } catch {
    return [];
  }
}
function saveOrders() {
  try {
    localStorage.setItem(ORDERS_KEY, JSON.stringify(orders.slice(0, 50)));
  } catch {
    /* ignore quota */
  }
}
const orders: Order[] = loadOrders();

function emitDaily() {
  const snapshot = daily.map((d) => ({ ...d }));
  dailyListeners.forEach((fn) => fn(snapshot));
}

export const demoStore = {
  subscribeDaily(cb: (items: DailyItem[]) => void): () => void {
    dailyListeners.add(cb);
    cb(daily.map((d) => ({ ...d })));
    return () => dailyListeners.delete(cb);
  },

  /** Decrement stock for daily lines (oversell-safe within the demo). */
  applyOrderToStock(lines: CartLine[]) {
    for (const line of lines) {
      if (line.type !== "daily") continue;
      const item = daily.find((d) => d.id === line.itemId);
      if (!item) continue;
      item.remainingQty = Math.max(0, item.remainingQty - line.qty);
      item.soldOut = item.remainingQty <= 0;
    }
    emitDaily();
  },

  /** Replace the whole daily list (admin editor in demo mode). */
  setDaily(items: DailyItem[]) {
    daily = items.map((d) => ({ ...d }));
    emitDaily();
  },

  getDaily(): DailyItem[] {
    return daily.map((d) => ({ ...d }));
  },

  addOrder(order: Order) {
    orders.unshift(order);
    saveOrders();
  },

  getOrders(): Order[] {
    return orders.map((o) => ({ ...o }));
  },

  getOrder(id: string): Order | null {
    const o = orders.find((x) => x.id === id);
    return o ? { ...o } : null;
  },

  setOrderStatus(id: string, patch: Partial<Order>) {
    const o = orders.find((x) => x.id === id);
    if (o) {
      Object.assign(o, patch);
      saveOrders();
    }
  },
};
